import cloneDeep from 'lodash/fp/cloneDeep';
import BuildAndPriceService from '../services/buildAndPriceService';

const initialState = {
  vehicleConfig: null,
  models: null, // returned from backend
  formattedModels: null, // grouped by name for the UI
  selectedModelId: null,
  selectedModelYears: null, // stores which model years are shown, toggle on model cards (selected)
  trims: null,
  selectedTrimId: null,
  viewTrimDetailsId: null, // Selected to view trim details on mobile (key features)
  vehicleImages: null,
  selectedOptions: {
    interiorColor: null,
    exteriorColor: null,
  },
};

const getters = {
  vehicleName: state =>
    state.vehicleConfig && state.vehicleConfig.vehicle
      ? `${state.vehicleConfig.vehicle.year} ${state.vehicleConfig.vehicle.make} ${state.vehicleConfig.vehicle.model} ${state.vehicleConfig.vehicle.trim} ${state.vehicleConfig.vehicle.style}`
      : null,
  vehiclePrice: state =>
    state.vehicleConfig && state.vehicleConfig.vehicle
      ? state.vehicleConfig.vehicle.vehiclePrice.base.msrp + state.vehicleConfig.vehicle.vehiclePrice.optionsMsrp
      : null,
  selectedModel: state =>
    state.models && state.selectedModelId ? state.models.filter(item => item.id === state.selectedModelId)[0] : null,
  selectedTrim: state =>
    state.trims && state.selectedTrimId
      ? state.trims.filter(item => item.vehicle.id === state.selectedTrimId)[0]
      : null,
  // eslint-disable-next-line no-shadow
  selectedModelName: (state, getters) =>
    getters.selectedModel
      ? `${getters.selectedModel.year} ${getters.selectedModel.make} ${getters.selectedModel.name}`
      : null,
  // eslint-disable-next-line no-shadow
  selectedModelPrice: (state, getters) => (getters.selectedModel ? getters.selectedModel.startingAtPrice : null),
  viewTrimDetails: state =>
    state.trims && state.viewTrimDetailsId
      ? state.trims.filter(item => item.vehicle.id === state.viewTrimDetailsId)[0]
      : null,
  colorizedVehicleImage: state =>
    state.vehicleImages &&
    state.selectedOptions.exteriorColor &&
    state.vehicleImages.filter(
      item => item.height === 480 && item.primaryColorOptionCode === state.selectedOptions.exteriorColor.value
    ) &&
    state.vehicleImages.filter(
      item => item.height === 480 && item.primaryColorOptionCode === state.selectedOptions.exteriorColor.value
    )[0]
      ? state.vehicleImages.filter(
          item => item.height === 480 && item.primaryColorOptionCode === state.selectedOptions.exteriorColor.value
        )[0].href
      : null,
};

const mutations = {
  SET_STATE(state, [key, data]) {
    state[key] = data;
  },
  SET_SELECTED_OPTION(state, [key, data]) {
    state.selectedOptions[key] = data;
  },
  RESET_SELECTED_OPTIONS(state) {
    state.selectedOptions = cloneDeep(initialState.selectedOptions);
  },
  RESET_TRIM(state) {
    state.selectedTrimId = null;
    state.vehicleImages = null;
    state.vehicleConfig = null;
  },
  SET_SELECTED_MODEL_YEAR(state, [key, data]) {
    state.selectedModelYears[key] = data;
  },
};

const actions = {
  async getBuildAndPriceModels({ rootState, commit }) {
    const { business } = rootState;
    const res = await BuildAndPriceService.getModels(
      business.api.key,
      business.api.externalID,
      rootState.user.language
    );
    commit('SET_STATE', ['models', res.data]);
  },
  async getBuildAndPriceTrims({ rootState, commit }, modelId) {
    const { business } = rootState;
    const res = await BuildAndPriceService.getTrims(
      business.api.key,
      business.api.externalID,
      modelId,
      rootState.user.language
    );
    commit('SET_STATE', ['trims', res.data]);
  },
  async getBuildAndPriceConfig({ rootState, commit }, trimId) {
    const { business } = rootState;
    const res = await BuildAndPriceService.getConfig(
      business.api.key,
      business.api.externalID,
      trimId,
      rootState.user.language
    );
    if (res.data.vehicle.vehicleImages && res.data.vehicle.vehicleImages.length > 0) {
      commit('SET_STATE', ['vehicleImages', res.data.vehicle.vehicleImages]);
    }
    commit('SET_STATE', ['vehicleConfig', res.data]);
  },
  async postVehicleOption({ rootState, commit }, payload) {
    const { business } = rootState;
    const res = await BuildAndPriceService.toggleVehicleOption(
      business.api.key,
      business.api.externalID,
      payload,
      rootState.user.language
    );
    commit('SET_STATE', ['vehicleConfig', res.data.vehicleConfiguration]);
    // We don't think this should happen so just going to log it for now just in case
    if (res.data.requiresConflictResolution) {
      this._vm.$log(
        `Vehicle requires conflict resolution, serialized value: ${payload.serializedValue}, option: ${payload.optionCode}`,
        new Error()
      );
    }
  },
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  mutations,
  actions,
  getters,
};
